/**
 * Lightweight, privacy-first analytics wrapper.
 *
 * Sends a beacon to /api/analytics/event for every interesting moment. The
 * payload is fully sanitized (no PII, no full URLs with query params other
 * than category, no IPs from frontend). Failures are silent.
 *
 * Usage:
 *   import { track } from "@/lib/analytics";
 *   track("offer_redeemed", { offer_id, business_name, category });
 *
 * Categories we currently track:
 *   page_view, register_start, register_complete, activate_complete,
 *   offer_redeemed, referral_share_opened, referral_share_clicked,
 *   drive_mode_toggled, push_opt_in, nearme_widget_shown, nearme_widget_dismissed
 */
import { api } from "@/lib/api";

const enabled = typeof window !== "undefined";
let sessionId = null;
function getSessionId() {
    if (sessionId) return sessionId;
    try {
        sessionId = sessionStorage.getItem("adn_sid");
        if (!sessionId) {
            sessionId = "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
            sessionStorage.setItem("adn_sid", sessionId);
        }
    } catch {
        sessionId = "s_anon";
    }
    return sessionId;
}

export function track(eventName, props = {}) {
    if (!enabled || !eventName) return;
    // Strip any keys that look like emails / phone / address — defense in depth
    const safe = {};
    for (const [k, v] of Object.entries(props || {})) {
        if (/email|phone|address|password|token|card/i.test(k)) continue;
        if (typeof v === "string" && v.length > 200) continue;
        safe[k] = v;
    }
    const body = {
        event: eventName,
        props: safe,
        sid: getSessionId(),
        path: window.location.pathname,
        ts: Date.now(),
    };
    // Fire-and-forget — never block UI
    try {
        api.post("/analytics/event", body).catch(() => { /* silent */ });
    } catch { /* silent */ }
}

export function trackPageView(path) {
    track("page_view", { path: path || window.location.pathname });
}
